/** Reddit runs only in an opaque, sandboxed, empty document — never in the
 * app's DOM. No identities, topics, URLs, or arbitrary event properties cross
 * this boundary. The public pixel ID is not a credential. */
const CLICK_KEY = "meriana_reddit_click";
const SENT_PREFIX = "meriana_reddit_signup_";
const MAX_CLICK_AGE = 30 * 24 * 60 * 60 * 1000;
const frames = new Set<HTMLIFrameElement>();
const visited = new Set<string>();

function optedOut(): boolean {
  if (typeof window === "undefined") return true;
  if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true) return true;
  try { return localStorage.getItem("meriana_ad_optout") === "1"; } catch { return true; }
}

export function stopRedditTracking(): void {
  for (const frame of frames) frame.remove();
  frames.clear();
  try { localStorage.removeItem(CLICK_KEY); } catch { /* fail closed */ }
  try { document.cookie = `${CLICK_KEY}=; path=/; max-age=0; SameSite=Lax`; } catch { /* cookies unavailable */ }
  // Mirror an explicit browser opt-out so server conversion gates also see it.
  if (optedOut()) {
    try { document.cookie = "meriana_ad_optout=1; path=/; max-age=31536000; SameSite=Lax; Secure"; } catch { /* cookies unavailable */ }
  }
}

function clickId(capture: boolean): string | undefined {
  try {
    const incoming = capture ? new URLSearchParams(location.search).get("rdt_cid") : null;
    if (incoming && /^[A-Za-z0-9._~-]{1,200}$/.test(incoming)) {
      localStorage.setItem(CLICK_KEY, JSON.stringify({ id: incoming, at: Date.now() }));
      document.cookie = `${CLICK_KEY}=${encodeURIComponent(JSON.stringify({ id: incoming, at: Date.now() }))}; path=/; max-age=2592000; SameSite=Lax; Secure`;
      return incoming;
    }
    const saved = JSON.parse(localStorage.getItem(CLICK_KEY) || "null");
    if (saved && typeof saved.id === "string" && /^[A-Za-z0-9._~-]{1,200}$/.test(saved.id)
        && typeof saved.at === "number" && Date.now() >= saved.at && Date.now() - saved.at < MAX_CLICK_AGE) {
      // Also migrate clicks captured by the previous browser-only version.
      const ttl = Math.max(1, Math.floor((MAX_CLICK_AGE - (Date.now() - saved.at)) / 1000));
      document.cookie = `${CLICK_KEY}=${encodeURIComponent(JSON.stringify(saved))}; path=/; max-age=${ttl}; SameSite=Lax; Secure`;
      return saved.id;
    }
    localStorage.removeItem(CLICK_KEY);
  } catch { /* storage unavailable: no click identifier */ }
  return undefined;
}

function send(event: "PageVisit" | "SignUp", id?: string, capture = false): boolean {
  if (optedOut()) { stopRedditTracking(); return false; }
  // Preview/local tests must not inflate the live advertising dataset.
  if (!import.meta.env.PROD) return false;
  try {
    const url = new URL("/reddit-pixel.html", location.origin);
    url.searchParams.set("event", event);
    if (id) url.searchParams.set("conversionId", id);
    const click = clickId(capture);
    if (click) url.searchParams.set("rdt_cid", click);
    const frame = document.createElement("iframe");
    // Intentionally omit allow-same-origin: the SDK cannot inspect the parent
    // DOM, cookies, storage, account details, or changing briefing views.
    frame.setAttribute("sandbox", "allow-scripts");
    frame.referrerPolicy = "no-referrer";
    frame.hidden = true;
    frame.title = "Reddit ad measurement";
    frame.src = url.href;
    frames.add(frame);
    document.body.appendChild(frame);
    setTimeout(() => { frame.remove(); frames.delete(frame); }, 30_000);
    return true;
  } catch { return false; /* advertising must never interrupt signup */ }
}

/** Marketing only. Resolve the server-authoritative opt-out before any SDK
 * request, including on fresh devices. 5xx/malformed/offline means no tracking. */
export async function trackRedditMarketingVisit(): Promise<void> {
  if (optedOut()) { stopRedditTracking(); return; }
  try {
    const response = await fetch("/api/auth/me", { credentials: "include" });
    let allowed = response.status === 401 || response.status === 403;
    if (response.ok) {
      const data = await response.json();
      allowed = data?.user?.ad_optout === false && data?.user?.is_teen === false;
    }
    if (!allowed || optedOut()) { stopRedditTracking(); return; }
    const path = location.pathname;
    if (!visited.has(path) && send("PageVisit", undefined, true)) visited.add(path);
  } catch { /* fail closed */ }
}

/** Called exclusively with the existing one-time, server-issued eligible
 * registration ID (verified adult, not opted out); not on clicks or logins. */
export function trackRedditSignup(eventId: unknown): void {
  if (optedOut()) { stopRedditTracking(); return; }
  if (typeof eventId !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(eventId)) return;
  try {
    const key = SENT_PREFIX + eventId;
    if (sessionStorage.getItem(key)) return;
    if (send("SignUp", eventId)) sessionStorage.setItem(key, "1");
  } catch { /* unavailable storage: skip, rather than duplicate */ }
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "meriana_ad_optout" && optedOut()) stopRedditTracking();
  });
}
